import NativeHelper from "./nativeUtils";

const DEVICE_TOKEN_KEY = "device_token";
const FCM_TOKEN_KEY = "fcm_token";

const fallbackUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const generateDeviceToken = () => {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : fallbackUUID();
};

export const saveDeviceToken = (token) => {
  if (!token) return;
  try {
    if (!localStorage.getItem(DEVICE_TOKEN_KEY)) {
      localStorage.setItem(DEVICE_TOKEN_KEY, token);
    }
  } catch (e) {
    // ignore storage errors (e.g., private mode)
  }
};

// Ensure a UUID device token exists and return it. Will NOT overwrite an existing token.
// Module-level fallback for environments where localStorage is unavailable
// (e.g. private/incognito mode). Reuses the same token for the entire session
// so repeated login attempts don't get a different device token each time.
let _inMemoryFallbackToken = null;

export const ensureDeviceToken = () => {
  try {
    let token = localStorage.getItem(DEVICE_TOKEN_KEY);
    if (token) return token;
    token = generateDeviceToken();
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
    return token;
  } catch (e) {
    // Private/incognito mode: reuse the same in-memory token for the session
    if (!_inMemoryFallbackToken) _inMemoryFallbackToken = generateDeviceToken();
    return _inMemoryFallbackToken;
  }
};

export const getDeviceToken = () => {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

// ─── FCM Token ────────────────────────────────────────────────────────────────
// Firebase Cloud Messaging push token.
// - On native apps (Flutter): retrieved from the native bridge.
// - On web browsers:          requested from Firebase Messaging SDK (web push).
// Stored separately from the UUID device token.
// Should only be synced and sent to the server ONCE (from the home page).

// Fetches the FCM token and caches it in localStorage.
// Priority: native bridge → web Firebase SDK → null.
export const syncFcmToken = async () => {
  // ── 1. Try native bridge first (Flutter / Android wrapper) ──────────────────
  try {
    const nativeToken = await NativeHelper.getFcmToken();
    if (nativeToken) {
      try {
        localStorage.setItem(FCM_TOKEN_KEY, nativeToken);
      } catch (e) {
        // ignore storage errors
      }
      return nativeToken;
    }
  } catch (e) {
    // Not a native app — fall through to web path
  }

  // ── 2. Web browser: use Firebase Messaging SDK ───────────────────────────────
  try {
    const { getWebMessaging } = await import("../../firebaseConfig");
    const { getToken } = await import("firebase/messaging");

    const messaging = await getWebMessaging();
    if (!messaging) return null;

    // Request notification permission if not already granted
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey || vapidKey === "YOUR_VAPID_KEY_HERE") {
      console.warn(
        "[FCM] REACT_APP_FIREBASE_VAPID_KEY is not set. " +
        "Get it from Firebase Console → Project Settings → Cloud Messaging → Web Push certificates."
      );
      return null;
    }

    const webToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      ),
    });

    if (webToken) {
      try {
        localStorage.setItem(FCM_TOKEN_KEY, webToken);
      } catch (e) {
        // ignore storage errors
      }
      return webToken;
    }
  } catch (e) {
    // Silently ignore — web push not available or user denied permission
  }

  return null;
};

// Returns the cached FCM token from localStorage (set by syncFcmToken).
export const getFcmToken = () => {
  try {
    return localStorage.getItem(FCM_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

